import { z } from 'zod';
import { isoDateTimeSchema } from '../common/coercion.js';
import { messageCorrelationIdSchema, uuidSchema } from '../common/correlation.js';
import { attemptOutcomeSchema, deadLetterResolutionSchema, flowIdSchema, messageDirectionSchema, messageStatusSchema, quarantineReasonSchema, systemSchema, } from '../common/systems.js';
/**
 * DTOs de la API de monitoreo.
 *
 * Son el contrato JSON entre `apps/api` y `apps/monitor` (y, mas adelante, el
 * servidor MCP de diagnostico). Espejan las tablas `message_log`,
 * `message_attempt`, `dead_letter` y `quarantine` con dos diferencias
 * deliberadas:
 *
 * - **camelCase**: las columnas son snake_case, el JSON es camelCase.
 * - **Fechas como texto ISO**: JSON no tiene tipo fecha; la UI las formatea a
 *   horario de Chile.
 *
 * Los objetos NO son estrictos a proposito: si la API agrega un campo, la UI lo
 * ignora en vez de romper la pantalla. Al reves si importa: si falta un campo o
 * cambia de tipo, la validacion falla y la UI lo dice.
 */
/**
 * Cabeceras HTTP ya redactadas.
 *
 * Acepta valores repetidos (un `array`, como los entrega Node) y los normaliza a
 * una sola cadena, que es lo que la UI muestra.
 */
export const httpHeadersSchema = z.record(z.string(), z
    .union([z.string(), z.array(z.string())])
    .transform((value) => (Array.isArray(value) ? value.join(', ') : value)));
/** Fila de `message_log`. */
export const messageLogEntrySchema = z.object({
    id: uuidSchema,
    correlationId: messageCorrelationIdSchema,
    direction: messageDirectionSchema,
    system: systemSchema,
    /** Flujo del plan. `null` cuando el mensaje no pertenece a ninguno todavia. */
    flow: flowIdSchema.nullable(),
    eventType: z.string().nullable(),
    idempotencyKey: z.string().nullable(),
    /** Payload tal cual llego o se envio. Inmutable: es la evidencia. */
    payloadRaw: z.unknown(),
    /** Payload validado y normalizado. `null` mientras no haya pasado por Zod. */
    payloadNormalized: z.unknown().nullable(),
    schemaVersion: z.string().nullable(),
    status: messageStatusSchema,
    attempts: z.number().int().nonnegative(),
    lastError: z.string().nullable(),
    /**
     * Observacion de un resultado ESPERADO que no es 2xx: el -4006 de SAP ("no hay
     * tipo de cambio de esa fecha") es la respuesta normal antes de cargarlo. NO es
     * un error: `lastError` sigue siendo solo para fallos, y una fila con nota esta
     * `processed`. Opcional para que una API anterior, que no la manda, valide.
     */
    note: z.string().nullable().optional(),
    httpStatus: z.number().int().nullable(),
    sourceIp: z.string().nullable(),
    headers: httpHeadersSchema.nullable(),
    occurredAt: z.string().nullable(),
    receivedAt: z.string(),
    processedAt: z.string().nullable(),
});
/** Fila de `message_attempt`: un intento de procesamiento. */
export const messageAttemptSchema = z.object({
    id: uuidSchema,
    messageId: uuidSchema,
    /** Numero de intento, 1-based. */
    attemptNo: z.number().int().positive(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    outcome: attemptOutcomeSchema,
    error: z.string().nullable(),
    /** Peticion enviada al sistema externo, con secretos redactados. */
    requestSnapshot: z.unknown().nullable(),
    /** Respuesta recibida, con secretos redactados. */
    responseSnapshot: z.unknown().nullable(),
});
/** Fila de `dead_letter`: el mensaje agoto sus reintentos. */
export const deadLetterSchema = z.object({
    id: uuidSchema,
    messageId: uuidSchema,
    /** Por que se rindio el procesamiento, en lenguaje de operador. */
    reason: z.string(),
    /** Historial de intentos congelado al momento de rendirse. */
    history: z.unknown().nullable(),
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
    /** Correo del usuario autorizado que la resolvio. */
    resolvedBy: z.string().nullable(),
    resolution: deadLetterResolutionSchema.nullable(),
    /**
     * Lo que escribio el operador al cerrarla: el motivo del descarte.
     *
     * `null` mientras esta abierta, y tambien en un reproceso, que no pide nota
     * porque el mensaje vuelve a la cola y se cuenta solo. Antes esto vivia
     * unicamente en el audit log del servidor: quien descartaba quedaba
     * registrado, pero POR QUE lo descartaba habia que ir a buscarlo a los logs.
     */
    resolutionNote: z.string().nullable(),
});
/** Fila de `quarantine`: payload que no paso la validacion del borde. */
export const quarantineEntrySchema = z.object({
    id: uuidSchema,
    /** `null` si el payload era tan invalido que ni llego a abrirse el mensaje. */
    messageId: uuidSchema.nullable(),
    system: systemSchema,
    reason: quarantineReasonSchema,
    /** `error.issues` de Zod, tal cual, para poder reproducir el rechazo. */
    zodIssues: z.unknown().nullable(),
    payloadRaw: z.unknown(),
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
    resolvedBy: z.string().nullable(),
    /**
     * Que se hizo con el payload, escrito por quien lo reviso.
     *
     * Es la nota que decide si el rechazo se corrigio en el contrato, si se mapeo
     * la entidad que faltaba o si se descarto a proposito. Sin ella la cuarentena
     * resuelta no se distingue de la cuarentena olvidada.
     */
    resolutionNote: z.string().nullable(),
});
/** Issue de Zod normalizado para mostrarlo en la pantalla de cuarentena. */
export const quarantineIssueSchema = z.object({
    path: z.string(),
    code: z.string(),
    message: z.string(),
    expected: z.string().nullable(),
    received: z.string().nullable(),
});
/** Sobre estandar de los listados paginados. */
export function paginatedSchema(itemSchema) {
    return z.object({
        items: z.array(itemSchema),
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        total: z.number().int().nonnegative(),
    });
}
/** Paginacion 1-indexada, como la muestra la UI. */
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(200).default(50),
});
/**
 * Filtros del listado de mensajes.
 *
 * Llegan por query string, asi que todo es texto: `page` y `pageSize` se
 * coercionan y el resto se valida contra el vocabulario de la plataforma. Un
 * filtro desconocido se rechaza en vez de ignorarse: si la UI manda
 * `status=procesado` hay que verlo, no devolver la lista completa.
 */
export const messageLogFilterSchema = paginationQuerySchema.extend({
    system: systemSchema.optional(),
    status: messageStatusSchema.optional(),
    flow: flowIdSchema.optional(),
    direction: messageDirectionSchema.optional(),
    /**
     * Correlation id exacto, o el numero de un pedido: entonces vienen los
     * mensajes de todos sus hilos (ver `flowRunFilterSchema.correlationId`).
     */
    correlationId: z.string().trim().min(1).max(128).optional(),
    eventType: z.string().trim().min(1).max(128).optional(),
    idempotencyKey: z.string().trim().min(1).max(255).optional(),
    /** Rango sobre `received_at`, en ISO UTC. */
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
});
/** Filtros de dead letters y cuarentena. */
export const incidentFilterSchema = paginationQuerySchema.extend({
    system: systemSchema.optional(),
    flow: flowIdSchema.optional(),
    /** `pending` = sin resolver; `resolved` = ya reprocesado o descartado. */
    resolution: z.enum(['pending', 'resolved']).optional(),
});
/** Listado paginado de mensajes. */
export const messageListResponseSchema = paginatedSchema(messageLogEntrySchema);
/** Detalle de un mensaje con todo lo que cuelga de el. */
export const messageDetailSchema = z.object({
    message: messageLogEntrySchema,
    attempts: z.array(messageAttemptSchema),
    deadLetter: deadLetterSchema.nullable(),
    quarantine: quarantineEntrySchema.nullable(),
});
/** Traza completa: todos los mensajes que comparten correlation id. */
export const messageTraceSchema = z.object({
    correlationId: messageCorrelationIdSchema,
    messages: z.array(messageLogEntrySchema),
});
/** Dead letter con su mensaje embebido (evita el N+1 en la tabla). */
export const deadLetterRowSchema = z.object({
    deadLetter: deadLetterSchema,
    message: messageLogEntrySchema.nullable(),
});
export const deadLetterListResponseSchema = paginatedSchema(deadLetterRowSchema);
/** Registro de cuarentena con su mensaje, si alcanzo a existir. */
export const quarantineRowSchema = z.object({
    quarantine: quarantineEntrySchema,
    message: messageLogEntrySchema.nullable(),
});
export const quarantineListResponseSchema = paginatedSchema(quarantineRowSchema);
/**
 * Cierre de una dead letter sin reprocesar. La razon es obligatoria: descartar
 * un mensaje sin decir por que es exactamente lo que este sistema evita.
 */
export const discardDeadLetterRequestSchema = z.object({
    reason: z
        .string()
        .trim()
        .min(3, 'Explica por que se descarta: queda guardado en la dead letter.')
        .max(512),
});
/** Marca un registro de cuarentena como revisado. */
export const resolveQuarantineRequestSchema = z.object({
    note: z
        .string()
        .trim()
        .min(3, 'Deja una nota de que se hizo con el payload: queda guardada en el registro.')
        .max(512),
});
/** Resultado de un reproceso pedido desde la UI. */
export const replayResultSchema = z.object({
    deadLetterId: uuidSchema,
    messageId: uuidSchema,
    status: messageStatusSchema,
    /** Correo del usuario autorizado que lo pidio. Queda en el audit log. */
    requestedBy: z.string(),
    requestedAt: z.string(),
});
/** Cuerpo de error de la API (RFC 7807 con extras). */
export const problemDetailsSchema = z.object({
    title: z.string().optional(),
    status: z.number().int().optional(),
    detail: z.string().optional(),
    /** Nest devuelve `message` como texto o como lista de errores de validacion. */
    message: z.union([z.string(), z.array(z.string())]).optional(),
    error: z.string().optional(),
    correlationId: z.string().optional(),
});
/**
 * El 400 de un payload que no cumple su schema, tal como lo emite
 * `ZodExceptionFilter` (`apps/api/src/common/zod-exception.filter.ts`): el
 * `problemDetails` de siempre mas `issues`, campo por campo. Un emisor (el POS)
 * los lee para marcar en su pantalla el campo culpable.
 */
export const validationProblemDetailsSchema = problemDetailsSchema.extend({
    issues: z.array(z.object({
        /** Ruta del campo (`dte.receptor.giro`), o `(raiz)`. */
        path: z.string(),
        /** Codigo de Zod (`invalid_type`, `custom`, `unrecognized_keys`...). */
        code: z.string(),
        message: z.string(),
        expected: z.string().nullable().optional(),
        received: z.string().nullable().optional(),
    })),
});
/**
 * Rutas de la API de monitoreo, relativas al prefijo global (`/api`).
 *
 * POR QUE VIVE EN EL CONTRATO Y NO EN CADA APLICACION: la UI llamaba a
 * `/monitoring/messages` mientras la API publicaba `/messages`, y nada lo
 * detectaba porque los tests de pantalla inyectan un `MonitorApi` falso. Toda la
 * UI de monitoreo estaba muerta contra la API real y el CI seguia en verde.
 *
 * Ahora hay una sola fuente: `apps/monitor` construye sus URLs a partir de esta
 * tabla y `apps/api` verifica en un test que el documento OpenAPI —que es el
 * contrato, por API First— declara exactamente estas rutas.
 *
 * Los parametros van en el estilo de OpenAPI (`{id}`), no en el de Express
 * (`:id`), porque el documento OpenAPI es el que manda.
 */
export const MONITORING_API_PATHS = {
    session: '/session',
    messages: '/messages',
    message: '/messages/{id}',
    /**
     * OJO con el nombre: la UI llama a esto "traza", pero la ruta de la API es
     * `messages/by-correlation`. Es justo el desajuste que rompio la pantalla.
     */
    messageTrace: '/messages/by-correlation/{correlationId}',
    /** Grafo de la corrida: la version dibujable de la traza. */
    flowGraph: '/flows/{correlationId}',
    /** Corridas: el message log agrupado por correlation id. */
    flowRuns: '/runs',
    /** Corridas anteriores y siguientes del mismo flujo, para navegar desde el grafo. */
    flowRunNeighbors: '/runs/{correlationId}/neighbors',
    /** Tablero de operacion: como viene el periodo y que exige atencion. */
    dashboard: '/dashboard',
    deadLetters: '/dead-letters',
    replayDeadLetter: '/dead-letters/{id}/replay',
    discardDeadLetter: '/dead-letters/{id}/discard',
    quarantine: '/quarantine',
    resolveQuarantine: '/quarantine/{id}/resolve',
};
/**
 * Reemplaza los parametros `{...}` de una ruta, escapandolos para la URL.
 *
 * Lanza si sobra o falta un parametro: una ruta con `{id}` sin sustituir es un
 * 404 silencioso contra la API real.
 */
export function monitoringApiPath(template, params = {}) {
    const used = new Set();
    const path = template.replace(/\{([A-Za-z0-9_]+)\}/g, (_match, name) => {
        const value = params[name];
        if (value === undefined) {
            throw new Error(`Falta el parametro "${name}" de la ruta de monitoreo ${template}.`);
        }
        used.add(name);
        return encodeURIComponent(value);
    });
    const extra = Object.keys(params).filter((name) => !used.has(name));
    if (extra.length > 0) {
        throw new Error(`La ruta de monitoreo ${template} no usa el parametro ${extra.join(', ')}: revisa el llamador.`);
    }
    return path;
}
//# sourceMappingURL=monitoring.js.map